import { z } from 'zod';
import { AREA_CODE_PATTERN } from './constants';

const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Warna format #RRGGBB');

// ------------------------------------------------------------------ areas (SET-05)

export interface AreaAdminView {
  code: string;
  name: string;
  prefix: string;
  color: string | null;
  assetCount: number;
  routeCount: number;
  hasBoundary: boolean;
  /** Technician SSO mapping for this area exists. */
  hasSsoMapping: boolean;
  userCount: number;
}

export const areaCreateSchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(
      AREA_CODE_PATTERN,
      'Huruf kecil dan angka, diawali huruf (sama dengan roleCode teknisi di Speedmax ID)',
    ),
  name: z.string().trim().min(2, 'Minimal 2 karakter').max(60),
  prefix: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{2,6}$/, '2–6 huruf besar/angka, mis. CPK'),
  color: hex,
});
export type AreaCreateInput = z.infer<typeof areaCreateSchema>;
export const areaUpdateSchema = areaCreateSchema.omit({ code: true });
export type AreaUpdateInput = z.infer<typeof areaUpdateSchema>;

// ------------------------------------------------------------------ master data (SET-01..04)

export interface AssetTypeAdminView {
  code: string;
  name: string;
  layer: string;
  supportsMonitoring: boolean;
  monitorByDefault: boolean;
  assetCount: number;
}
export const assetTypeUpdateSchema = z.object({ monitorByDefault: z.boolean() });

export interface SplitterView {
  id: number;
  category: 'main' | 'odp';
  ratio: string;
  lossDb: number;
}
export const splitterUpdateSchema = z.object({ lossDb: z.number().min(0).max(40) });

export interface FiberColorView {
  order: number;
  name: string;
  hex: string;
}
export const fiberColorSchema = z.object({ name: z.string().trim().min(2).max(30), hex });

export interface CableTypeView {
  code: string;
  name: string;
  attenuationDbPerKm: number;
  pricePerM: number | null;
  routeCount: number;
}
export const cableTypeSchema = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,12}$/, '2–12 huruf besar/angka'),
  name: z.string().trim().min(2).max(60),
  attenuationDbPerKm: z.number().min(0).max(5),
  pricePerM: z.number().min(0).max(10_000_000).nullable(),
});
export type CableTypeInput = z.infer<typeof cableTypeSchema>;

export interface MasterData {
  assetTypes: AssetTypeAdminView[];
  splitters: SplitterView[];
  fiberColors: FiberColorView[];
  cableTypes: CableTypeView[];
}

// ------------------------------------------------------------------ monitoring defaults (SET-06)

export const monitoringDefaultsSchema = z.object({
  intervalSec: z.number().int().min(30).max(86_400),
  failThreshold: z.number().int().min(1).max(10),
  latencyWarnMs: z.number().int().min(1).max(60_000),
  lossWarnPct: z.number().int().min(1).max(100),
});
export type MonitoringDefaults = z.infer<typeof monitoringDefaultsSchema>;
